import { enUS, es, ar, fr, af } from 'date-fns/locale';
import type { Locale } from 'date-fns';

// Type definitions
// US + Africa target set (7 languages). English is PRIMARY (default, fallback,
// unprefixed at root). zu (isiZulu) has no date-fns locale — it aliases enUS
// wherever a date-fns Locale is required.
export type SupportedLanguage = 'en' | 'es' | 'fr' | 'ar' | 'pt-pt' | 'af' | 'zu';

// Month names for all supported languages
export const MONTH_NAMES: Record<SupportedLanguage, string[]> = {
  en: [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ],
  es: [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
  ],
  fr: [
    'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
  ],
  ar: [
    'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
  ],
  'pt-pt': [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
  ],
  af: [
    'Januarie', 'Februarie', 'Maart', 'April', 'Mei', 'Junie',
    'Julie', 'Augustus', 'September', 'Oktober', 'November', 'Desember'
  ],
  zu: [
    'Januwari', 'Februwari', 'Mashi', 'Ephreli', 'Meyi', 'Juni',
    'Julayi', 'Agasti', 'Septhemba', 'Okthoba', 'Novemba', 'Disemba'
  ],
};

// Map language codes to date-fns locales
// Note: date-fns has limited locale support, so we use closest available locales.
// Portuguese (Portugal) uses Spanish locale as it's a Romance language with similar structure.
// isiZulu (zu) has no date-fns locale — alias to enUS.
export const DATE_LOCALES: Record<SupportedLanguage, Locale> = {
  en: enUS,
  es: es,
  fr: fr,
  ar: ar,
  'pt-pt': es, // Portuguese Portugal uses Spanish locale as fallback (similar Romance language)
  af: af,
  zu: enUS, // isiZulu has no date-fns locale — alias to English (US)
};

// Map language codes to locale strings for toLocaleDateString
export const LOCALE_STRINGS: Record<SupportedLanguage, string> = {
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  ar: 'ar-SA',
  'pt-pt': 'pt-PT',
  af: 'af-ZA',
  zu: 'zu-ZA',
};

// Map language codes to Accept-Language header values for APIs
export const ACCEPT_LANGUAGE_HEADERS: Record<SupportedLanguage, string> = {
  en: 'en',
  es: 'es',
  fr: 'fr',
  ar: 'ar',
  'pt-pt': 'pt-PT',
  af: 'af',
  zu: 'zu',
};

// Map language codes to display names
export const LANGUAGE_DISPLAY_NAMES: Record<SupportedLanguage, string> = {
  en: 'EN English',
  es: 'ES Español',
  fr: 'FR Français',
  ar: 'AR العربية',
  'pt-pt': 'PT Português (PT)',
  af: 'AF Afrikaans',
  zu: 'ZU isiZulu',
};

// Normalize language code to standard format
export const normalizeLanguageCode = (code: string | null | undefined): SupportedLanguage | null => {
  if (!code) return null;

  const normalized = code.toLowerCase().replace('_', '-');

  // Handle Portuguese variants — all Portuguese now maps to European Portuguese
  // (pt-PT, lusophone Africa). pt / pt-br / pt-pt / pt-portugal all collapse here.
  if (normalized === 'pt' || normalized.startsWith('pt-') || normalized.startsWith('pt')) {
    return 'pt-pt';
  }

  if (isSupportedLanguage(normalized)) {
    return normalized as SupportedLanguage;
  }

  return null;
};

// Check if a language code is supported
export const isSupportedLanguage = (code: string): code is SupportedLanguage => {
  return ['en', 'es', 'fr', 'ar', 'pt-pt', 'af', 'zu'].includes(code);
};

// Get month names for a language
export const getMonthNames = (language: SupportedLanguage): string[] => {
  return MONTH_NAMES[language] || MONTH_NAMES.en;
};

// Get date locale for a language
export const getDateLocale = (language: SupportedLanguage) => {
  return DATE_LOCALES[language] || DATE_LOCALES.en;
};

// Get locale string for toLocaleDateString
export const getLocaleString = (language: SupportedLanguage): string => {
  return LOCALE_STRINGS[language] || LOCALE_STRINGS.en;
};

// Get Accept-Language header value
export const getAcceptLanguageHeader = (language: SupportedLanguage): string => {
  return ACCEPT_LANGUAGE_HEADERS[language] || ACCEPT_LANGUAGE_HEADERS.en;
};

// Get display name for a language
export const getLanguageDisplayName = (language: SupportedLanguage): string => {
  return LANGUAGE_DISPLAY_NAMES[language] || language;
};

// Format date with month name in a specific language
export const formatDateWithMonthName = (date: Date, language: SupportedLanguage): string => {
  const monthNames = getMonthNames(language);
  const day = date.getDate();
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

// Get all supported languages
export const getAllSupportedLanguages = (): SupportedLanguage[] => {
  return ['en', 'es', 'fr', 'ar', 'pt-pt', 'af', 'zu'];
};

// PDF text translations for all languages
export const PDF_TEXT_TRANSLATIONS: Record<SupportedLanguage, Record<string, string>> = {
  en: {
    offerValidUntil: 'Offer valid until:',
    placementPlan: 'Placement Plan',
    cameras: 'Cameras',
    recommendations: 'Recommendations:',
    notes: 'Notes:',
    offerPreparedBy: 'Offer prepared by',
    pricesChangeNotice: 'Prices may change after the offer validity date.',
    yourCompany: 'Your Company',
    no: 'No.',
    productService: 'Product/Service',
    qty: 'Qty',
    unitNet: 'Unit Net',
    unitGross: 'Unit Gross',
    totalNet: 'Total Net',
    totalGross: 'Total Gross',
    subtotalNet: 'Subtotal Net:',
    vat: 'VAT:',
    total: 'TOTAL',
    net: 'NET',
    gross: 'GROSS',
    item: 'Item',
    position: 'Position',
    clientData: 'CLIENT DATA',
    clientDetails: 'Client Details',
    projectInformation: 'Project Information',
    pricingVariants: 'Pricing Variants',
    equipmentAndMaterials: 'Equipment & Materials',
    device: 'Device',
    quantity: 'Quantity',
    price: 'Price',
    date: 'Date:',
    company: 'Company:',
    contact: 'Contact:',
    email: 'Email:',
    phone: 'Phone:',
    project: 'Project:',
    fences: 'Fences:',
    buildings: 'Buildings:',
    devices: 'Devices:',
    basePrice: 'Base price:',
    perCamera: 'Per camera:',
    page: 'Page',
    of: 'of',
    monthlyFee: 'Monthly fee:',
    contractPeriod: 'Contract period:',
    indefinite: 'indefinite',
    freeConnection: 'Free connection:',
    connectionCost: 'Connection cost:',
    yes: 'Yes',
    netSuffix: 'net',
    fovNote: 'FOV ranges shown are approximate and may vary based on installation conditions.',
  },
  es: {
    offerValidUntil: 'Oferta válida hasta:',
    placementPlan: 'Plan de Colocación',
    cameras: 'Cámaras',
    recommendations: 'Recomendaciones:',
    notes: 'Notas:',
    offerPreparedBy: 'Oferta preparada por',
    pricesChangeNotice: 'Los precios pueden cambiar después de la fecha de validez de la oferta.',
    yourCompany: 'Su Empresa',
    no: 'Nº',
    productService: 'Producto/Servicio',
    qty: 'Ctd',
    unitNet: 'Precio Unitario Neto',
    unitGross: 'Precio Unitario Bruto',
    totalNet: 'Total Neto',
    totalGross: 'Total Bruto',
    subtotalNet: 'Subtotal Neto:',
    vat: 'IVA:',
    total: 'TOTAL',
    net: 'Neto',
    gross: 'Bruto',
    item: 'Artículo',
    position: 'Posición',
    clientData: 'DATOS DEL CLIENTE',
    clientDetails: 'Datos del Cliente',
    projectInformation: 'Información del Proyecto',
    pricingVariants: 'Variantes de Precio',
    equipmentAndMaterials: 'Equipos y Materiales',
    device: 'Dispositivo',
    quantity: 'Cantidad',
    price: 'Precio',
    date: 'Fecha:',
    company: 'Empresa:',
    contact: 'Contacto:',
    email: 'Correo:',
    phone: 'Teléfono:',
    project: 'Proyecto:',
    fences: 'Cercas:',
    buildings: 'Edificios:',
    devices: 'Dispositivos:',
    basePrice: 'Precio base:',
    perCamera: 'Por cámara:',
    page: 'Página',
    of: 'de',
    monthlyFee: 'Cuota mensual:',
    contractPeriod: 'Período del contrato:',
    indefinite: 'indefinido',
    freeConnection: 'Conexión gratuita:',
    connectionCost: 'Costo de conexión:',
    yes: 'Sí',
    netSuffix: 'neto',
    fovNote: 'Los rangos de FOV mostrados son aproximados y pueden variar según las condiciones de instalación.',
  },
  fr: {
    offerValidUntil: 'Offre valable jusqu\'au :',
    placementPlan: 'Plan de placement',
    cameras: 'Caméras',
    recommendations: 'Recommandations :',
    notes: 'Notes :',
    offerPreparedBy: 'Offre préparée par',
    pricesChangeNotice: 'Les prix peuvent changer après la date de validité de l\'offre.',
    yourCompany: 'Votre Entreprise',
    no: 'N°',
    productService: 'Produit/Service',
    qty: 'Qté',
    unitNet: 'Prix unitaire HT',
    unitGross: 'Prix unitaire TTC',
    totalNet: 'Total HT',
    totalGross: 'Total TTC',
    subtotalNet: 'Sous-total HT :',
    vat: 'TVA :',
    total: 'TOTAL',
    net: 'HT',
    gross: 'TTC',
    item: 'Article',
    position: 'Position',
    clientData: 'DONNÉES CLIENT',
    clientDetails: 'Détails du client',
    projectInformation: 'Informations du projet',
    pricingVariants: 'Variantes de prix',
    equipmentAndMaterials: 'Équipements et matériaux',
    device: 'Appareil',
    quantity: 'Quantité',
    price: 'Prix',
    date: 'Date :',
    company: 'Entreprise :',
    contact: 'Contact :',
    email: 'E-mail :',
    phone: 'Téléphone :',
    project: 'Projet :',
    fences: 'Clôtures :',
    buildings: 'Bâtiments :',
    devices: 'Appareils :',
    basePrice: 'Prix de base :',
    perCamera: 'Par caméra :',
    page: 'Page',
    of: 'sur',
    monthlyFee: 'Frais mensuels :',
    contractPeriod: 'Durée du contrat :',
    indefinite: 'indéterminée',
    freeConnection: 'Connexion gratuite :',
    connectionCost: 'Coût de connexion :',
    yes: 'Oui',
    netSuffix: 'HT',
    fovNote: 'Les portées de FOV indiquées sont approximatives et peuvent varier selon les conditions d\'installation.',
  },
  ar: {
    offerValidUntil: 'العرض صالح حتى:',
    placementPlan: 'خطة التوزيع',
    cameras: 'الكاميرات',
    recommendations: 'التوصيات:',
    notes: 'ملاحظات:',
    offerPreparedBy: 'العرض مُعد من قبل',
    pricesChangeNotice: 'قد تتغير الأسعار بعد تاريخ انتهاء صلاحية العرض.',
    yourCompany: 'شركتك',
    no: 'رقم',
    productService: 'المنتج/الخدمة',
    qty: 'الكمية',
    unitNet: 'سعر الوحدة الصافي',
    unitGross: 'سعر الوحدة الإجمالي',
    totalNet: 'الإجمالي الصافي',
    totalGross: 'الإجمالي الكلي',
    subtotalNet: 'المجموع الفرعي الصافي:',
    vat: 'ضريبة القيمة المضافة:',
    total: 'الإجمالي',
    net: 'صافي',
    gross: 'إجمالي',
    item: 'بند',
    position: 'موضع',
    clientData: 'بيانات العميل',
    clientDetails: 'تفاصيل العميل',
    projectInformation: 'معلومات المشروع',
    pricingVariants: 'خيارات التسعير',
    equipmentAndMaterials: 'المعدات والمواد',
    device: 'جهاز',
    quantity: 'الكمية',
    price: 'السعر',
    date: 'التاريخ:',
    company: 'الشركة:',
    contact: 'جهة الاتصال:',
    email: 'البريد الإلكتروني:',
    phone: 'الهاتف:',
    project: 'المشروع:',
    fences: 'الأسوار:',
    buildings: 'المباني:',
    devices: 'الأجهزة:',
    basePrice: 'السعر الأساسي:',
    perCamera: 'لكل كاميرا:',
    page: 'صفحة',
    of: 'من',
    monthlyFee: 'الرسوم الشهرية:',
    contractPeriod: 'مدة العقد:',
    indefinite: 'غير محدد',
    freeConnection: 'توصيل مجاني:',
    connectionCost: 'تكلفة التوصيل:',
    yes: 'نعم',
    netSuffix: 'صافي',
    fovNote: 'نطاقات زاوية الرؤية المعروضة تقريبية وقد تختلف حسب ظروف التركيب.',
  },
  'pt-pt': {
    offerValidUntil: 'Proposta válida até:',
    placementPlan: 'Plano de Colocação',
    cameras: 'Câmaras',
    recommendations: 'Recomendações:',
    notes: 'Notas:',
    offerPreparedBy: 'Proposta elaborada por',
    pricesChangeNotice: 'Os preços podem ser alterados após a data de validade da proposta.',
    yourCompany: 'A Sua Empresa',
    no: 'N.º',
    productService: 'Produto/Serviço',
    qty: 'Qtd.',
    unitNet: 'Preço Unitário Líquido',
    unitGross: 'Preço Unitário Bruto',
    totalNet: 'Total Líquido',
    totalGross: 'Total Bruto',
    subtotalNet: 'Subtotal Líquido:',
    vat: 'IVA:',
    total: 'TOTAL',
    net: 'Líquido',
    gross: 'Bruto',
    item: 'Item',
    position: 'Posição',
    clientData: 'DADOS DO CLIENTE',
    clientDetails: 'Dados do Cliente',
    projectInformation: 'Informações do Projeto',
    pricingVariants: 'Variantes de Preço',
    equipmentAndMaterials: 'Equipamentos e Materiais',
    device: 'Dispositivo',
    quantity: 'Quantidade',
    price: 'Preço',
    date: 'Data:',
    company: 'Empresa:',
    contact: 'Contacto:',
    email: 'E-mail:',
    phone: 'Telefone:',
    project: 'Projeto:',
    fences: 'Vedações:',
    buildings: 'Edifícios:',
    devices: 'Dispositivos:',
    basePrice: 'Preço base:',
    perCamera: 'Por câmara:',
    page: 'Página',
    of: 'de',
    monthlyFee: 'Mensalidade:',
    contractPeriod: 'Período do contrato:',
    indefinite: 'indeterminado',
    freeConnection: 'Ligação gratuita:',
    connectionCost: 'Custo de ligação:',
    yes: 'Sim',
    netSuffix: 'líquido',
    fovNote: 'Os alcances de FOV apresentados são aproximados e podem variar consoante as condições de instalação.',
  },
  af: {
    offerValidUntil: 'Aanbod geldig tot:',
    placementPlan: 'Plasingsplan',
    cameras: 'Kameras',
    recommendations: 'Aanbevelings:',
    notes: 'Notas:',
    offerPreparedBy: 'Aanbod opgestel deur',
    pricesChangeNotice: 'Pryse kan verander na die geldigheidsdatum van die aanbod.',
    yourCompany: 'Jou Maatskappy',
    no: 'Nr.',
    productService: 'Produk/Diens',
    qty: 'Hvh.',
    unitNet: 'Eenheid Netto',
    unitGross: 'Eenheid Bruto',
    totalNet: 'Totaal Netto',
    totalGross: 'Totaal Bruto',
    subtotalNet: 'Subtotaal Netto:',
    vat: 'BTW:',
    total: 'TOTAAL',
    net: 'Netto',
    gross: 'Bruto',
    item: 'Item',
    position: 'Posisie',
    clientData: 'KLIËNTDATA',
    clientDetails: 'Kliëntbesonderhede',
    projectInformation: 'Projekinligting',
    pricingVariants: 'Prysvariante',
    equipmentAndMaterials: 'Toerusting en Materiale',
    device: 'Toestel',
    quantity: 'Hoeveelheid',
    price: 'Prys',
    date: 'Datum:',
    company: 'Maatskappy:',
    contact: 'Kontak:',
    email: 'E-pos:',
    phone: 'Telefoon:',
    project: 'Projek:',
    fences: 'Heinings:',
    buildings: 'Geboue:',
    devices: 'Toestelle:',
    basePrice: 'Basisprys:',
    perCamera: 'Per kamera:',
    page: 'Bladsy',
    of: 'van',
    monthlyFee: 'Maandelikse fooi:',
    contractPeriod: 'Kontraktydperk:',
    indefinite: 'onbepaald',
    freeConnection: 'Gratis verbinding:',
    connectionCost: 'Verbindingskoste:',
    yes: 'Ja',
    netSuffix: 'netto',
    fovNote: 'Die getoonde FOV-reikwydtes is benaderend en kan wissel na gelang van installasietoestande.',
  },
  zu: {
    offerValidUntil: 'Isipho sisebenza kuze kube:',
    placementPlan: 'Uhlelo Lokubeka',
    cameras: 'Amakhamera',
    recommendations: 'Izincomo:',
    notes: 'Amanothi:',
    offerPreparedBy: 'Isipho silungiswe ngu',
    pricesChangeNotice: 'Amanani angashintsha ngemva kosuku lokuphela kwesipho.',
    yourCompany: 'Inkampani Yakho',
    no: 'Nom.',
    productService: 'Umkhiqizo/Isevisi',
    qty: 'Ubuningi',
    unitNet: 'Iyunithi (Net)',
    unitGross: 'Iyunithi (Gross)',
    totalNet: 'Isamba (Net)',
    totalGross: 'Isamba (Gross)',
    subtotalNet: 'Isamba esincane (Net):',
    vat: 'VAT:',
    total: 'ISAMBA',
    net: 'Net',
    gross: 'Gross',
    item: 'Into',
    position: 'Isikhundla',
    clientData: 'IDATHA YEKHASIMENDE',
    clientDetails: 'Imininingwane Yekhasimende',
    projectInformation: 'Ulwazi Lwephrojekthi',
    pricingVariants: 'Izinketho Zamanani',
    equipmentAndMaterials: 'Izinsiza Nezimpahla',
    device: 'Idivayisi',
    quantity: 'Ubuningi',
    price: 'Intengo',
    date: 'Usuku:',
    company: 'Inkampani:',
    contact: 'Oxhumana naye:',
    email: 'I-imeyili:',
    phone: 'Ifoni:',
    project: 'Iphrojekthi:',
    fences: 'Izintango:',
    buildings: 'Izakhiwo:',
    devices: 'Amadivayisi:',
    basePrice: 'Inani lesisekelo:',
    perCamera: 'Nge-khamera ngayinye:',
    page: 'Ikhasi',
    of: 'kwe',
    monthlyFee: 'Imali yenyanga:',
    contractPeriod: 'Isikhathi senkontileka:',
    indefinite: 'okungapheli',
    freeConnection: 'Ukuxhumana kwamahhala:',
    connectionCost: 'Izindleko zokuxhumana:',
    yes: 'Yebo',
    netSuffix: 'net',
    fovNote: 'Amabanga e-FOV aboniswayo angaqagela futhi angahluka ngokuya ngezimo zokufaka.',
  },
};

// Get PDF text translation for a language
export const getPDFText = (language: SupportedLanguage, key: string): string => {
  return PDF_TEXT_TRANSLATIONS[language]?.[key] || PDF_TEXT_TRANSLATIONS.en[key] || key;
};
